/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { createContext, useContext, useState, useRef, useEffect, useLayoutEffect, useId } from "react";
import { useClickOutside } from "../../hooks/useClickOutside";
import { useEscapeKey } from "../../hooks/a11y/useEscapeKey";
import styled, { css } from "styled-components";

const PLACEMENTS = ["top", "bottom", "bottom-left", "top-left", "bottom-right", "top-right", "left", "right"];
const OFFSET = 4;

const StyledPopover = styled.div`
  position: relative;
`;

const StyledPopoverContent = styled.div`
  position: absolute;
  border-radius: 8px;
  text-align: left;
  box-shadow: 2px 2px 15px rgba(${({ theme }) => theme.colors.text.rgb}, 0.1);
  background: rgba(${({ theme }) => theme.colors.surface.rgba});
  z-index: 1000;

  &[hidden] {
    display: none;
  }

  ${({ $placement, $triggerRect, $contentDimensions, theme }) => {
    if (!$triggerRect) return {};

    const { width, height } = $triggerRect;
    const $isMobile = window.innerWidth < parseInt(theme.breakpoints.sm.replace("px", ""));

    if ($isMobile) {
      return {
        left: "auto",
        right: $placement === "bottom-left" ? 'auto' : 0,
        top: height + OFFSET,
        width: `calc(100dvw - ${width}px)`,
      };
    }

    switch ($placement) {
      case "top":
        return css`
          left: ${width / 2}px;
          top: ${-$contentDimensions.height - OFFSET}px;
        `;
      case "bottom":
        return css`
          left: ${width / 2}px;
          top: ${height + OFFSET}px;
        `;
      case "left":
        return css`
          right: ${width + OFFSET}px;
          top: ${height / 2}px;
        `;
      case "right":
        return css`
          left: ${width + OFFSET}px;
          top: ${height / 2}px;
        `;
      case "top-left":
        return css`
          left: 0;
          top: ${-$contentDimensions.height - OFFSET}px;
        `;
      case "top-right":
        return css`
          right: 0;
          top: ${-$contentDimensions.height - OFFSET}px;
        `;
      case "bottom-left":
        return css`
          left: 0;
          top: ${height + OFFSET}px;
        `;
      case "bottom-right":
        return css`
          right: 0;
          top: ${height + OFFSET}px;
        `;
      default:
        return {};
    }
  }};
`;

const PopoverContext = createContext();

const PopoverProvider = ({ children, placement, onOpenChange }) => {
  if (!PLACEMENTS.includes(placement)) {
    throw new Error(`Invalid placement: ${placement}. Must be one of ${PLACEMENTS.join(", ")}`);
  }

  const [show, setShow] = useState(false);
  const triggerRef = useRef(null);
  const contentRef = useRef(null);
  const contentId = useId();

  const handleSetShow = (value) => {
    setShow((prev) => typeof value === "function" ? value(prev) : value);
  };

  useEffect(() => {
    if (onOpenChange) onOpenChange(show);
  }, [show]);

  useClickOutside(() => handleSetShow(false), [triggerRef.current, contentRef.current]);
  useEscapeKey(() => handleSetShow(false), show);

  return (
    <PopoverContext.Provider value={{ show, setShow: handleSetShow, triggerRef, contentRef, contentId, placement }}>
      {children}
    </PopoverContext.Provider>
  );
};

const usePopover = () => {
  const context = useContext(PopoverContext);
  if (!context) {
    throw new Error("usePopover must be used within a PopoverProvider");
  }
  return context;
};

const PopoverTrigger = ({ children }) => {
  const { setShow, triggerRef, show, contentId } = usePopover();

  const handleClick = () => setShow((prev) => !prev);

  return React.cloneElement(children, {
    ref: triggerRef,
    "aria-haspopup": "dialog",
    "aria-expanded": show,
    "aria-controls": contentId,
    onClick: (e) => {
      if (children.props.onClick) {
        e.stopPropagation();
        children.props.onClick(e);
      }
      handleClick();
    },
  });
};

const PopoverContent = ({ children, className, ...props }) => {
  const { show, contentRef, contentId, placement, triggerRef } = usePopover();
  const [triggerRect, setTriggerRect] = useState(null);
  const [contentDimensions, setContentDimensions] = useState(null);

  useLayoutEffect(() => {
    if (!show || !contentRef.current || !triggerRef.current) return;

    setContentDimensions(contentRef.current.getBoundingClientRect());
    setTriggerRect(triggerRef.current.getBoundingClientRect());
  }, [show]);

  useEffect(() => {
    if (!show) return;

    const updateDimensions = () => {
      if (!contentRef.current || !triggerRef.current) return;
      setContentDimensions(contentRef.current.getBoundingClientRect());
      setTriggerRect(triggerRef.current.getBoundingClientRect());
    };

    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, [show]);

  return (
    <StyledPopoverContent
      ref={contentRef}
      id={contentId}
      className={className}
      hidden={!show}
      $placement={placement}
      $triggerRect={triggerRect}
      $contentDimensions={contentDimensions}
      {...props}
    >
      {show ? children : null}
    </StyledPopoverContent>
  );
};

const Popover = ({ children, placement = "bottom", onOpenChange }) => (
  <PopoverProvider placement={placement} onOpenChange={onOpenChange}>
    <StyledPopover>{children}</StyledPopover>
  </PopoverProvider>
);

export { Popover, PopoverTrigger, PopoverContent };
